import React, { useState, useRef } from "react";
import {
  Box,
  TextField,
  IconButton,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  Send as SendIcon,
  Stop as StopIcon,
  Clear as ClearIcon,
  Mic as MicIcon,
} from "@mui/icons-material";

const ChatInput = ({
  onSendMessage,
  loading,
  onStopGeneration,
  placeholder = "Posez une question concernant Smart HR...",
  maxLength = 500,
}) => {
  const theme = useTheme();
  const [inputValue, setInputValue] = useState("");
  const textareaRef = useRef(null);

  const handleSubmit = (e) => {
    if (e) e.preventDefault();

    if (inputValue.trim() && !loading) {
      onSendMessage(inputValue);
      setInputValue("");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleClear = () => {
    setInputValue("");

    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        bgcolor: "background.paper",
        borderTop: "1px solid",
        borderColor: "divider",
      }}
    >
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          maxWidth: "900px",
          mx: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        <Box
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "flex-end",
            gap: 1,
          }}
        >
          <TextField
            inputRef={textareaRef}
            fullWidth
            multiline
            maxRows={6}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={loading}
            variant="outlined"
            slotProps={{
              htmlInput: {
                maxLength,
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "24px",
                bgcolor: "grey.50",
                pl: 3,
                pr: 7,
                py: 2,
                transition: "all 0.2s",
                boxShadow: theme.shadows[1],
                "&:hover": {
                  bgcolor: "background.paper",
                  boxShadow: theme.shadows[3],
                },
                "&.Mui-focused": {
                  bgcolor: "background.paper",
                  boxShadow: theme.shadows[4],
                },
              },
            }}
          />

          <Box
            sx={{
              position: "absolute",
              right: 8,
              bottom: 8,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            {inputValue.length > 0 && (
              <IconButton
                size="small"
                onClick={handleClear}
                disabled={loading}
                sx={{
                  color: "text.disabled",
                  "&:hover": { color: "text.primary" },
                }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            )}

            {loading && onStopGeneration ? (
              <IconButton
                color="error"
                onClick={onStopGeneration}
                sx={{
                  bgcolor: "error.light",
                  color: "white",
                  "&:hover": { bgcolor: "error.main" },
                }}
              >
                <StopIcon />
              </IconButton>
            ) : (
              <IconButton
                color="primary"
                onClick={handleSubmit}
                disabled={loading || !inputValue.trim()}
                sx={{
                  bgcolor: inputValue.trim()
                    ? "primary.main"
                    : "action.disabledBackground",
                  color: "white",
                  "&:hover": { bgcolor: "primary.dark" },
                  "&.Mui-disabled": { color: "white", opacity: 0.7 },
                }}
              >
                <SendIcon />
              </IconButton>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Appuyez sur Entrée pour envoyer, Maj+Entrée pour sauter une ligne
            </Typography>

            {inputValue.length > 0 && (
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={500}
              >
                {inputValue.length} / {maxLength}
              </Typography>
            )}
          </Box>

          <IconButton disabled size="small" sx={{ color: "text.disabled" }}>
            <MicIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

export default ChatInput;
